import { AsyncPipe, NgClass } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  EmbeddedViewRef,
  HostListener,
  Inject,
  OnDestroy,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IconComponent } from '@aptem/components';
import { Store } from '@ngrx/store';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { PageUtils } from '../../../common/page.utils';
import { AssessmentPanelUtils } from '../../../common/ui.utils';
import { TestletType } from '../../../constants/testlet-type.enum';
import { APP_CONFIG, AppConfig } from '../../../models/app-config';
import { AssessmentPlayerService } from '../../../services/player.service';
import { noNav } from '../../../store/actions/page-content.actions';
import { PageContentState } from '../../../store/models/assessment-models';
import { selectCurrentUpdateAssessmentState } from '../../../store/selectors/assessment.selectors';
import { selectActionButtons, selectDisplayActionButtons } from '../../../store/selectors/page-content.selectors';
import { selectLoadingState } from '../../../store/selectors/page-loading.selectors';
import { PageComponent } from '../../common/page/page.component';
import { PageConfig } from '../../common/page/page.models';
import { TestletActionButtonsConfig } from '../../common/testlet-action-buttons/models/testlet-action-buttons.models';
import { TestletActionButtonsComponent } from '../../common/testlet-action-buttons/testlet-action-buttons.component';
import { BaseTestletHandler } from '../base-testlet-handler';

interface SuppressedNativeInputConfig {
  inputmode: string | null;
  readOnly: boolean;
  touchListener?: EventListener;
  focusListener?: EventListener;
  tabIndex: string | null;
  ariaHidden: string | null;
}

@Component({
  selector: 'number-recall',
  templateUrl: './number-recall.component.html',
  styleUrls: ['./number-recall.component.scss', '../styles/testlet.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, PageComponent, TestletActionButtonsComponent, NgClass, IconComponent],
})
export class NumberRecallComponent extends BaseTestletHandler implements OnDestroy, AfterViewInit {
  @ViewChild('numberButtonsTemplate', { static: true }) private readonly numberButtonsTemplate: TemplateRef<any>;

  protected readonly numberpad = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];
  protected readonly backspace = 'backspace';

  private currentSpanIndex = 0;
  private totalSpanCount = 0;

  private currentSubmitButton: HTMLButtonElement;
  private lastKeypadView?: EmbeddedViewRef<any>;
  private lastKeypadRoot?: HTMLElement;
  private readonly hiddenModalKeypadClass = 'aptem-hidden-learnosity-keypad';
  private modalKeypadObserver?: MutationObserver;
  private hiddenModalKeypads = new Set<HTMLElement>();
  private readonly modalKeypadSelectors = [
    '.lrn-math-modal-keypad',
    '.lrn-math-response-modal__keypad',
    '.lrn-math-response-modal',
    '.lrn-math-response-modal__container',
    '.lrn-math-response-modal__header',
    '[class^="lrn-math-response-modal__"]',
  ];
  private readonly suppressedNativeInputs = new Map<
    HTMLInputElement | HTMLTextAreaElement,
    SuppressedNativeInputConfig
  >();
  private readonly isMobileDevice: boolean;
  private readonly isIOSDevice: boolean;
  private readonly nativeFocusSweepTimers = new Set<number>();
  private readonly focusInListener = (event: FocusEvent) => {
    if (!this.isMobileDevice) {
      return;
    }

    if (!(event.target instanceof HTMLElement)) {
      return;
    }

    if (!this.isLearnosityFocusTarget(event.target)) {
      return;
    }

    this.forceBlur(event.target);
    this.suppressNativeKeyboard(event.target);
    this.deferNativeInputBlur(event.target);
    this.scheduleNativeFocusSweep();
  };

  private isQuestionActive = false;
  protected isSubmitEnabled = false;
  private readonly buttonPressed = new BehaviorSubject<string>('');

  protected updatedAssessmentConfig = this.store.select(selectCurrentUpdateAssessmentState);
  protected displayActionButtons$: Observable<boolean> = this.store.select(selectDisplayActionButtons);
  protected testletActionButtonsConfig$: Observable<TestletActionButtonsConfig> = this.store
    .select(selectActionButtons)
    .pipe(
      map((buttons) => ({
        buttonTrackingContext: 'number-recall',
        ...buttons,
      })),
    );
  protected pageConfig$: Observable<PageConfig> = this.store
    .select(selectLoadingState)
    .pipe(map((state) => ({ loading: state.loadingData.loading })));

  constructor(
    @Inject(DOCUMENT) private document: Document,
    protected playerService: AssessmentPlayerService,
    protected store: Store<PageContentState>,
    private pageUtils: PageUtils,
    @Inject(APP_CONFIG) private readonly config: AppConfig,
    private readonly viewContainerRef: ViewContainerRef,
  ) {
    super(playerService, store);
    const userAgent = this.document.defaultView?.navigator?.userAgent ?? '';
    this.isIOSDevice = /iPad|iPhone|iPod/i.test(userAgent);
    this.isMobileDevice = this.isIOSDevice || /Android/i.test(userAgent);
  }

  ngAfterViewInit() {
    this.document.body.classList.add('aptem-number-recall-active');
    // Set Event Listeners action methods here.
    this.eventHandlers = {
      questionLoadEventNotifier$: (itemPosition) => {
        this.isQuestionActive = true;
        this.isSubmitEnabled = false;
        this.setQuestionPageControls(itemPosition);

        this.setFocusOnFirstEmptyBox();
        this.setNumberpadEventListeners();
        this.setBackspaceEventListener();
        this.insertClearButton();
        this.insertSubmitButton();
        this.insertButtonSet();
      },
      questionChangeEventNotifier$: (response) => this.toggleSubmitButton(response.responses),
    };
    this.initializeModalKeypadObserver();
    if (this.isMobileDevice) {
      this.document.addEventListener('focusin', this.focusInListener, true);
    }
    this.updatedAssessmentConfig.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((updateAssessment) => {
      if (
        updateAssessment != null &&
        !updateAssessment.loading &&
        updateAssessment.error == null &&
        updateAssessment.data?.config != null &&
        updateAssessment.data?.nextTestlet == 'Number Recall'
      ) {
        const testlet = updateAssessment.data;
        this.initializeListeners(
          JSON.parse(updateAssessment.data.config),
          'NumberRecall',
          testlet.sessionId,
          testlet.assessmentId,
        );
      } else {
        this.pageUtils.navigateToIntroductionPage(!updateAssessment?.loading);
      }
    });

    this.buttonClickSubject
      .pipe(debounceTime(this.config.debounceTime), takeUntilDestroyed(this.destroyRef))
      .subscribe((type) => {
        this.handleDebouncedButtonClick(type);
      });
  }

  ngOnDestroy() {
    this.document.body.classList.remove('aptem-number-recall-active');
    this.destroyModalKeypadObserver();
    if (this.isMobileDevice) {
      this.document.removeEventListener('focusin', this.focusInListener, true);
    }
    this.clearNativeFocusSweeps();
    this.restoreNativeKeyboardOverrides();
    this.resetModalKeypadStyles();
    this.onDestroy();
  }

  @HostListener('window:keyup', ['$event'])
  public keyUpEventListener(event: KeyboardEvent) {
    event.preventDefault();
    const isEnterKeyDown = event.key === ' ' || event.key === 'Enter';
    if (isEnterKeyDown) {
      (this.document.activeElement as HTMLElement)?.click();
    }
  }

  @HostListener('pointerdown', ['$event'])
  public responseClickHandler(event: any) {
    if (event.target.tagName !== 'INPUT') {
      return;
    }
    if (this.playerService.isLastInstruction()) {
      this.store.dispatch(noNav());
    }
    this.navigateToNextItem();
  }

  /**
   * @description Will insert the corresponding digit at current position
   */
  protected handleNumber(i: string, event: Event | null) {
    const buttons = this.getOriginalKeypadButtons();
    const filteredButtons = Array.from(buttons).filter((button) => button.getAttribute('data-symbol-key') === i);

    if (this.currentSpanIndex + 1 > this.totalSpanCount) {
      if (event) {
        if (event.cancelable) {
          event.preventDefault();
        }
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === 'function') {
          event.stopImmediatePropagation();
        }
      }
      return;
    }

    this.prepareForCustomInput(event ?? undefined);

    if (filteredButtons.length === 1) {
      filteredButtons[0].click();
      this.visualizeButtonPress(i);
      this.releaseNativeFocus();
      this.scheduleNativeFocusSweep();
    }

    this.currentSpanIndex += 1;
    this.focusOnSpan(this.currentSpanIndex);
    if (this.currentSpanIndex === this.totalSpanCount) {
      this.enableNumbPadButtons(false);
    }
  }

  /**
   * @description Will remove the latest inserted digit
   */
  protected handleBackspace(event?: Event, skipSchedule = false, skipPrepare = false) {
    if (this.currentSpanIndex === 0) {
      if (event && event.cancelable) {
        event.preventDefault();
      }
      if (event && typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }
      return;
    }

    if (!skipPrepare) {
      this.prepareForCustomInput(event);
    }

    this.currentSpanIndex -= 1;
    this.focusOnSpan(this.currentSpanIndex);
    setTimeout(() => {
      const buttons = this.getOriginalKeypadButtons();
      const filteredButtons = Array.from(buttons).filter(
        (button) => button.getAttribute('data-symbol-key') === this.backspace,
      );

      if (filteredButtons.length === 1) {
        filteredButtons[0].click();
        this.visualizeButtonPress(this.backspace);
        this.releaseNativeFocus();
        if (!skipSchedule) {
          this.scheduleNativeFocusSweep();
        }
      }
    }, 0);
  }

  /**
   * @description Will reset the response entered by the user.
   * This is achieved by setting focus to each response box and then press the backspace via code.
   */
  protected async handleClearAll(event?: Event) {
    this.prepareForCustomInput(event);
    while (this.currentSpanIndex !== 0) {
      await new Promise((resolve) => setTimeout(resolve, 0));
      this.handleBackspace(undefined, true, true);
    }
    this.releaseNativeFocus();
    this.scheduleNativeFocusSweep();
  }

  /**
   * @description A method to visualize button clicks within keyboard
   */
  protected isPressed(btn: string) {
    return this.buttonPressed.pipe(map((b) => b === btn));
  }

  private visualizeButtonPress(symb: string) {
    // Visualize numeric button press
    this.buttonPressed.next(symb);
    setTimeout(() => this.buttonPressed.next(''), 100);
  }

  /**
   * @description Get buttons from the keypad provided by Learnosity, which is hidden for user interaction.
   */
  private getOriginalKeypadButtons() {
    const keypadWrapper =
      this.document.getElementsByClassName('lds-flexible-container')[this.currentQuestionIndex] ??
      this.document.getElementsByClassName('lds-flexible-container')[0];
    return keypadWrapper?.getElementsByTagName('button');
  }

  /**
   * @description Enable/disable "Submit" button based on the "Attempted Status" of the current question.
   */
  private toggleSubmitButton(responses: string[]) {
    const incomplete = responses.some((value) => value === null || value === '');
    this.setDisabledState(incomplete);
    this.enableNumbPadButtons(incomplete);
    this.isSubmitEnabled = !incomplete;
  }

  setDisabledState(disabled: boolean) {
    if (this.currentSubmitButton) {
      this.currentSubmitButton.disabled = disabled;
    }
  }

  /**
   * @description Identifies the first empty response box and set focus to that.
   * Set the total response box count and calculate the number of empty response boxes.
   * These values will be used to reset the response.
   */
  private setFocusOnFirstEmptyBox() {
    this.restoreNativeKeyboardOverrides();
    const inputWrapper = this.document.getElementsByClassName('lrn_response_input')?.[this.currentQuestionIndex];
    const emptySpanCount = inputWrapper?.getElementsByClassName('mq-empty')?.length;
    const spanList = inputWrapper?.getElementsByClassName('mq-textarea');
    this.totalSpanCount = spanList.length;
    const firstEmptyInput = this.totalSpanCount - emptySpanCount;
    this.currentSpanIndex = firstEmptyInput;
    this.applyNativeKeyboardSuppression(spanList);
    this.primeNativeKeyboardSuppression();
    this.focusOnSpan(firstEmptyInput);
  }

  /**
   * @description Bind event listeners to the Learnosity number pad.
   * This event listener will be used to shift focus to the next response box everytime when user modify the answer value.
   */
  private setNumberpadEventListeners() {
    const keypadWrapper = this.document.getElementsByClassName('lrn_input_ui')[this.currentQuestionIndex];
    const buttons = keypadWrapper?.getElementsByClassName('lds-btn lds-btn-outline-secondary');
    for (let i = 0; i < buttons.length; i++) {
      const button = buttons[i];
      const id = (button as HTMLButtonElement).getAttribute('data-symbol-key');
      if (id === 'backspace') {
        return;
      }
      button.setAttribute('id', `${this.currentQuestionIndex}-${id}`);

      // Triggers a 'click' event.
      button.addEventListener('pointerdown', (event: any) => {
        if (this.currentSpanIndex + 1 > this.totalSpanCount) {
          event.stopImmediatePropagation();
          return;
        }
        this.focusOnSpan(this.currentSpanIndex);
        this.currentSpanIndex += 1;
        if (this.currentSpanIndex === this.totalSpanCount) {
          this.enableNumbPadButtons(false);
          return;
        }
      });
    }
  }

  private insertButtonSet() {
    if (this.lastKeypadRoot) {
      if (this.lastKeypadRoot.parentElement) {
        this.lastKeypadRoot.remove();
      }

      if (this.lastKeypadView) {
        this.lastKeypadView.destroy();
      }

      this.lastKeypadRoot = undefined;
      this.lastKeypadView = undefined;
    }

    const buttonSetView = this.viewContainerRef.createEmbeddedView(this.numberButtonsTemplate);
    const root = buttonSetView.rootNodes[0] as HTMLElement;
    const targetKeyboard = this.resolveKeyboardHost();
    const insertionParent = this.resolveInsertionParent(targetKeyboard);
    const nextSibling = this.resolveInsertionReference(targetKeyboard, insertionParent);

    if (targetKeyboard?.classList.contains('lrn-math-modal-keypad')) {
      this.hideModalKeypad(targetKeyboard);
    } else {
      this.resetModalKeypadStyles();
    }

    if (insertionParent) {
      insertionParent.insertBefore(root, nextSibling);
    }

    this.lastKeypadView = buttonSetView;
    this.lastKeypadRoot = root;
    if (this.isMobileDevice) {
      this.lastKeypadRoot.setAttribute('tabindex', '-1');
      this.focusKeypadRoot();
    }

    this.currentSubmitButton = root.querySelector<HTMLButtonElement>(`button.btn-submit`) as HTMLButtonElement;
  }

  /**
   * @description Binds an event listener to the backspace button in Learnosity number pad.
   * This event listeners will update empty response box count.
   * It also shift focus to last non empty response box, everytime when user press on backspace button.
   */
  private setBackspaceEventListener() {
    const keypadWrapper = this.getModalKeypad() ?? this.resolveKeyboardHost();
    const backspace = keypadWrapper?.querySelector('[data-symbol-key="backspace"]') as HTMLElement;

    // Triggers a 'click' event.
    backspace?.addEventListener('pointerdown', () => {
      if (this.currentSpanIndex === 0) {
        return;
      }
      this.currentSpanIndex -= 1;
      this.focusOnSpan(this.currentSpanIndex);
    });
  }

  /**
   * @description Insert a clear button to the Learnosity number pad.
   */
  private insertClearButton() {
    const id = `numberpad-clear-btn-${this.currentQuestionIndex}`;
    const clearBtn = AssessmentPanelUtils.createElement(this.document, 'button', 'clear', id);
    const keyboard = this.resolveKeyboardHost();
    keyboard?.insertAdjacentElement('beforeend', clearBtn);
    this.setButtonListener(id, 'clear');
    AssessmentPanelUtils.setElementAttribute(
      clearBtn,
      'lrn_btn_grid lrn-btn-grid-dir font-size-16',
      'clear button',
      '-1',
    );
  }

  /**
   * @description Insert a submit button to the Learnosity number pad.
   */
  private insertSubmitButton() {
    const id = `submit-button-${this.currentQuestionIndex}`;
    const submitBtn = AssessmentPanelUtils.createElement(this.document, 'button', 'submit', id);
    const keyboard = this.resolveKeyboardHost();
    keyboard?.insertAdjacentElement('beforeend', submitBtn);
    this.setButtonListener(id, 'submit');
    AssessmentPanelUtils.setElementAttribute(
      submitBtn,
      'btn btn-submit rounded-pill font-size-18 text-capitalize text-white border-0',
      'submit button',
      '1',
      true,
    );
  }

  /**
   * @description Disables all the on screen number pad buttons when there is no empty response boxes left.
   * This will prevent user from entering digites greater than 9.
   * @param enable Value which controls the state of the number pad buttons.
   */
  private enableNumbPadButtons(enable: boolean) {
    const buttons = this.lastKeypadRoot?.getElementsByClassName('numeric-button') as HTMLCollectionOf<Element>;
    for (let i = 0; i < buttons.length; i++) {
      const button = buttons[i];
      enable ? this.enableNumbPadButton(button) : this.disableNumbPadButton(button);
    }
  }

  private enableNumbPadButton(button: Element) {
    (button as HTMLButtonElement)?.removeAttribute('style');
  }

  private disableNumbPadButton(button: Element) {
    (button as HTMLButtonElement)?.setAttribute('style', 'pointer-events:none');
  }

  private resolveKeyboardHost(): HTMLElement | null {
    const inlineKeyboard = this.document.getElementsByClassName('lrn_input_ui')?.[this.currentQuestionIndex] as
      | HTMLElement
      | undefined;
    if (inlineKeyboard) {
      return inlineKeyboard;
    }

    return this.getModalKeypad();
  }

  private resolveInsertionParent(targetKeyboard: HTMLElement | null): HTMLElement | null {
    if (targetKeyboard?.parentElement && targetKeyboard.parentElement.tagName !== 'BODY') {
      return targetKeyboard.parentElement;
    }

    return (
      (this.document.querySelector('.lrn-player-container.number-recall') as HTMLElement) ??
      (this.document.getElementById('lrn-player-container') as HTMLElement) ??
      targetKeyboard?.parentElement ??
      this.document.body
    );
  }

  private resolveInsertionReference(targetKeyboard: HTMLElement | null, parent: HTMLElement | null): ChildNode | null {
    if (!targetKeyboard || !parent) {
      return null;
    }

    if (targetKeyboard.parentElement === parent) {
      return targetKeyboard.nextSibling;
    }

    return null;
  }

  private hideModalKeypad(modalKeypad: HTMLElement) {
    if (modalKeypad.classList.contains(this.hiddenModalKeypadClass)) {
      return;
    }

    this.hiddenModalKeypads.add(modalKeypad);
    modalKeypad.classList.add(this.hiddenModalKeypadClass);
    modalKeypad.setAttribute('aria-hidden', 'true');
    modalKeypad.setAttribute('inert', '');

    const modalWrapper = modalKeypad.closest<HTMLElement>('.lrn-math-response-modal');
    if (modalWrapper && modalWrapper !== modalKeypad) {
      this.hiddenModalKeypads.add(modalWrapper);
      modalWrapper.classList.add(this.hiddenModalKeypadClass);
      modalWrapper.setAttribute('aria-hidden', 'true');
      modalWrapper.setAttribute('inert', '');
    }
  }

  private getModalKeypad(): HTMLElement | null {
    for (const selector of this.modalKeypadSelectors) {
      const matches = this.document.querySelectorAll<HTMLElement>(selector);
      if (matches.length === 0) {
        continue;
      }

      const candidate = matches[this.currentQuestionIndex] ?? matches[0];
      if (candidate && this.isModalWrapper(candidate)) {
        continue;
      }

      return candidate ?? null;
    }

    return null;
  }

  private getAllModalKeypads(): HTMLElement[] {
    const results = new Set<HTMLElement>();
    for (const selector of this.modalKeypadSelectors) {
      for (let i = 0; i < this.document.querySelectorAll<HTMLElement>(selector).length; i++) {
        let element = this.document.querySelectorAll<HTMLElement>(selector)[i];
        results.add(element);
      }
    }
    for (const element of this.getWrapperCandidates()) {
      results.add(element);
    }
    return Array.from(results);
  }

  private getWrapperCandidates(): HTMLElement[] {
    const wrappers: HTMLElement[] = [];
    for (
      let i = 0;
      i <
      this.document.querySelectorAll<HTMLElement>('.lrn-math-response-modal, [class^="lrn-math-response-modal__"]')
        .length;
      i++
    ) {
      let element = this.document.querySelectorAll<HTMLElement>(
        '.lrn-math-response-modal, [class^="lrn-math-response-modal__"]',
      )[i];
      if (this.isModalWrapper(element)) {
        wrappers.push(element);
      }
    }
    return wrappers;
  }

  private isModalWrapper(element: HTMLElement): boolean {
    const classNames = Array.from(element.classList);

    if (classNames.some((cls) => cls.includes('__keypad'))) {
      return false;
    }

    if (classNames.includes('lrn-math-response-modal')) {
      return true;
    }

    return classNames.some((cls) => cls.startsWith('lrn-math-response-modal__'));
  }

  private resetModalKeypadStyles() {
    for (const keypad of this.hiddenModalKeypads) {
      keypad.classList.remove(this.hiddenModalKeypadClass);
      if (keypad.getAttribute('aria-hidden') === 'true') {
        keypad.removeAttribute('aria-hidden');
      }
      if (keypad.hasAttribute('inert')) {
        keypad.removeAttribute('inert');
      }
    }
    this.hiddenModalKeypads.clear();
  }

  private initializeModalKeypadObserver() {
    if (this.modalKeypadObserver) {
      return;
    }

    const isModalKeypad = (element: HTMLElement) =>
      this.modalKeypadSelectors.some((selector) => element.matches(selector));

    const applyToNode = (node: Node) => {
      if (!(node instanceof HTMLElement)) {
        return;
      }

      if (isModalKeypad(node)) {
        this.hideModalKeypad(node);
        return;
      }

      for (const selector of this.modalKeypadSelectors) {
        for (let i = 0; i < node.querySelectorAll<HTMLElement>(selector).length; i++) {
          let element = node.querySelectorAll<HTMLElement>(selector)[i];
          this.hideModalKeypad(element);
        }
      }

      this.applyNativeKeyboardSuppressionToNode(node);
    };

    this.modalKeypadObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(applyToNode);
        }

        if (
          mutation.type === 'attributes' &&
          mutation.target instanceof HTMLElement &&
          isModalKeypad(mutation.target)
        ) {
          this.hideModalKeypad(mutation.target);
          this.applyNativeKeyboardSuppressionToNode(mutation.target);
          return;
        }

        if (mutation.type === 'attributes' && mutation.target instanceof HTMLElement) {
          this.applyNativeKeyboardSuppressionToNode(mutation.target);
        }
      }
    });

    this.modalKeypadObserver.observe(this.document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'aria-hidden'],
    });

    for (const element of this.getAllModalKeypads()) {
      this.hideModalKeypad(element);
    }
    if (this.isMobileDevice) {
      for (
        let i = 0;
        i <
        this.document.querySelectorAll<HTMLElement>(
          '.lrn_response_input .lrn_focusable, .lrn_response_input input, .lrn_response_input textarea',
        ).length;
        i++
      ) {
        let element = this.document.querySelectorAll<HTMLElement>(
          '.lrn_response_input .lrn_focusable, .lrn_response_input input, .lrn_response_input textarea',
        )[i];
        this.suppressNativeKeyboard(element);
      }
      this.primeNativeKeyboardSuppression();
    }
  }

  private destroyModalKeypadObserver() {
    this.modalKeypadObserver?.disconnect();
    this.modalKeypadObserver = undefined;
  }

  /**
   * @description Set focus to the response boxes based on the index position.
   */
  private focusOnSpan(spanIndex: number) {
    const inputWrapper = this.document.getElementsByClassName('lrn_response_input')?.[this.currentQuestionIndex];
    const spanList = inputWrapper?.getElementsByClassName('mq-textarea');
    const focusableElement = spanList[spanIndex]?.getElementsByClassName('lrn_focusable')[0] as HTMLElement;
    this.suppressNativeKeyboard(focusableElement);
    focusableElement?.focus();
    this.deferNativeInputBlur(focusableElement);
  }

  private applyNativeKeyboardSuppression(spanList: HTMLCollectionOf<Element> | undefined) {
    if (!this.isMobileDevice || !spanList || spanList.length === 0) {
      return;
    }

    for (let i = 0; i < spanList.length; i++) {
      const span = spanList[i];
      const focusable = span.getElementsByClassName('lrn_focusable')?.[0] as HTMLElement;
      this.suppressNativeKeyboard(focusable);
    }
  }

  private primeNativeKeyboardSuppression() {
    if (!this.isMobileDevice) {
      return;
    }

    const candidates = this.document.querySelectorAll<HTMLElement>(
      '.lrn_response_input textarea, .lrn_response_input input, textarea.mq-textarea, input.mq-textarea',
    );

    for (let i = 0; i < candidates.length; i++) {
      const element = candidates[i];
      this.suppressNativeKeyboard(element);
    }
  }

  private applyNativeKeyboardSuppressionToNode(node: HTMLElement) {
    if (!this.isMobileDevice) {
      return;
    }

    if (this.isLearnosityFocusTarget(node)) {
      this.suppressNativeKeyboard(node);
    }

    if (!node.closest('.lrn_response_input') && !node.querySelector('.lrn_response_input')) {
      return;
    }

    for (let i = 0; i < node.querySelectorAll<HTMLElement>('.lrn_focusable, input, textarea').length; i++) {
      let element = node.querySelectorAll<HTMLElement>('.lrn_focusable, input, textarea')[i];
      this.suppressNativeKeyboard(element);
    }
  }

  private isLearnosityFocusTarget(element: HTMLElement): boolean {
    if (element.classList?.contains('lrn_focusable')) {
      return true;
    }

    if (!(element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement)) {
      return false;
    }

    return element.closest('.lrn_response_input') != null;
  }

  private deferNativeInputBlur(focusableElement: HTMLElement | null | undefined) {
    if (!this.isMobileDevice || !focusableElement) {
      return;
    }

    const nativeInput = this.findNativeInput(focusableElement);
    if (!nativeInput) {
      return;
    }

    setTimeout(() => {
      if (!this.isMobileDevice) {
        return;
      }
      if (!this.document.contains(nativeInput)) {
        return;
      }
      this.suppressNativeKeyboard(nativeInput);
      this.forceBlur(nativeInput);
      if (this.document.contains(focusableElement)) {
        this.forceBlur(focusableElement);
      }
      this.focusKeypadRoot();
    }, 0);
  }

  private suppressNativeKeyboard(focusableElement: HTMLElement | null | undefined) {
    if (!this.isMobileDevice || !focusableElement) {
      return;
    }

    const nativeInput = this.findNativeInput(focusableElement);
    if (!nativeInput || this.suppressedNativeInputs.has(nativeInput)) {
      return;
    }

    const config: SuppressedNativeInputConfig = {
      inputmode: nativeInput.getAttribute('inputmode'),
      readOnly: nativeInput.hasAttribute('readonly'),
      tabIndex: nativeInput.getAttribute('tabindex'),
      ariaHidden: nativeInput.getAttribute('aria-hidden'),
    };

    nativeInput.setAttribute('inputmode', 'none');
    nativeInput.setAttribute('readonly', 'true');
    nativeInput.setAttribute('tabindex', '-1');
    nativeInput.setAttribute('aria-hidden', 'true');

    if (this.isIOSDevice) {
      const touchListener: EventListener = (event) => {
        const touchEvent = event as TouchEvent;
        if (touchEvent.cancelable) {
          touchEvent.preventDefault();
        }
      };

      config.touchListener = touchListener;
      nativeInput.addEventListener('touchstart', touchListener, { capture: true, passive: false });
    }

    const focusListener: EventListener = (event) => {
      const focusEvent = event as FocusEvent;
      if (typeof focusEvent.stopImmediatePropagation === 'function') {
        focusEvent.stopImmediatePropagation();
      }
      this.forceBlur(nativeInput);
      this.focusKeypadRoot();
      this.deferNativeInputBlur(nativeInput);
    };

    nativeInput.addEventListener('focus', focusListener, true);
    config.focusListener = focusListener;

    this.suppressedNativeInputs.set(nativeInput, config);
  }

  private findNativeInput(element: HTMLElement | null): HTMLInputElement | HTMLTextAreaElement | null {
    if (!element) {
      return null;
    }

    if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
      return element;
    }

    const container =
      element.closest('.lrn_response_input, .lrn_math_editable, .lrn_textinput') ?? element.parentElement;
    if (container) {
      const scoped = container.querySelector('textarea, input');
      if (scoped instanceof HTMLInputElement || scoped instanceof HTMLTextAreaElement) {
        return scoped;
      }
    }

    const fallback = this.document.querySelector(
      '.mq-editable-field textarea, .mq-editable-field input, .lrn_response_input textarea, .lrn_response_input input',
    ) as HTMLInputElement | HTMLTextAreaElement;

    if (fallback instanceof HTMLInputElement || fallback instanceof HTMLTextAreaElement) {
      return fallback;
    }

    return null;
  }

  private restoreNativeKeyboardOverrides() {
    if (this.suppressedNativeInputs.size === 0) {
      return;
    }

    for (const [input, config] of this.suppressedNativeInputs) {
      if (config.inputmode == null) {
        input.removeAttribute('inputmode');
      } else {
        input.setAttribute('inputmode', config.inputmode);
      }

      if (!config.readOnly) {
        input.removeAttribute('readonly');
      }

      if (config.tabIndex == null) {
        input.removeAttribute('tabindex');
      } else {
        input.setAttribute('tabindex', config.tabIndex);
      }

      if (config.ariaHidden == null) {
        input.removeAttribute('aria-hidden');
      } else {
        input.setAttribute('aria-hidden', config.ariaHidden);
      }

      if (config.touchListener) {
        input.removeEventListener('touchstart', config.touchListener, true);
      }

      if (config.focusListener) {
        input.removeEventListener('focus', config.focusListener, true);
      }
    }

    this.suppressedNativeInputs.clear();
  }

  private forceBlur(element: HTMLElement | null | undefined) {
    if (!element) {
      return;
    }

    if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
      try {
        element.blur();
      } catch {}

      try {
        element.setSelectionRange?.(0, 0);
      } catch {}
    } else if (element instanceof HTMLElement) {
      try {
        element.blur();
      } catch {}
    }

    try {
      const selection = this.document.getSelection?.();
      selection?.removeAllRanges?.();
    } catch {}
  }

  private releaseNativeFocus() {
    if (!this.isMobileDevice) {
      return;
    }

    const activeElement = this.document.activeElement as HTMLElement | null;
    if (activeElement && this.isLearnosityFocusTarget(activeElement)) {
      this.forceBlur(activeElement);
      this.deferNativeInputBlur(activeElement);
      return;
    }

    this.focusKeypadRoot();
  }

  private prepareForCustomInput(event?: Event) {
    if (!this.isMobileDevice) {
      return;
    }

    if (event) {
      if (event.cancelable) {
        event.preventDefault();
      }
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }
    }

    const activeElement = this.document.activeElement as HTMLElement | null;
    if (activeElement) {
      if (this.isLearnosityFocusTarget(activeElement)) {
        this.forceBlur(activeElement);
        this.suppressNativeKeyboard(activeElement);
      } else if (!this.lastKeypadRoot?.contains(activeElement)) {
        this.forceBlur(activeElement);
      }
    }

    this.clearNativeFocusSweeps();
    this.focusKeypadRoot();
  }

  private scheduleNativeFocusSweep(iterations = 6, delay = 16) {
    if (!this.isMobileDevice) {
      return;
    }

    const windowRef = this.document.defaultView;
    if (!windowRef) {
      return;
    }

    this.clearNativeFocusSweeps();

    const run = (remaining: number) => {
      const active = this.document.activeElement as HTMLElement | null;
      if (active && this.isLearnosityFocusTarget(active)) {
        this.forceBlur(active);
        this.suppressNativeKeyboard(active);
      }

      this.primeNativeKeyboardSuppression();
      this.focusKeypadRoot();

      if (remaining <= 0) {
        return;
      }

      const timeoutId = windowRef.setTimeout(() => {
        this.nativeFocusSweepTimers.delete(timeoutId);
        run(remaining - 1);
      }, delay);

      this.nativeFocusSweepTimers.add(timeoutId);
    };

    run(iterations);
  }

  private clearNativeFocusSweeps() {
    if (this.nativeFocusSweepTimers.size === 0) {
      return;
    }

    const windowRef = this.document.defaultView;
    for (const id of this.nativeFocusSweepTimers) {
      if (windowRef) {
        windowRef.clearTimeout(id);
      }
    }
    this.nativeFocusSweepTimers.clear();
  }

  private focusKeypadRoot() {
    if (!this.isMobileDevice) {
      return;
    }

    const target =
      this.lastKeypadRoot ?? (this.document.querySelector('.buttons-container') as HTMLElement) ?? undefined;

    if (!target) {
      return;
    }

    if (!target.hasAttribute('tabindex')) {
      target.setAttribute('tabindex', '-1');
    }

    try {
      target.focus({ preventScroll: true });
    } catch {
      target.focus();
    }
  }

  /**
   * @description Attach click event handler to the the custom button based on the ID.
   * Prevents invoking of any 'touchend' event handlers and then triggers 'click' event.
   */
  private setButtonListener(elementId: string, label: string) {
    this.document.getElementById(elementId)?.addEventListener('pointerdown', () => {
      if (label === 'clear') {
        this.clearResponses();
      }
      if (label === 'submit') {
        this.navigateToNextItem();
      }
    });
  }

  @HostListener('window:keydown', ['$event'])
  public keyDownEventListener(event: KeyboardEvent) {
    if (!this.isQuestionActive) {
      return;
    }

    event.preventDefault();

    const isDigit = this.numberpad.includes(event.key) || event.key === '0';
    if (isDigit) {
      this.handleNumber(event.key, null);
      return;
    }

    if (event.key === 'Backspace') {
      this.handleBackspace();
      return;
    }

    if (['Enter', ' '].includes(event.key)) {
      if (this.isSubmitEnabled) {
        this.navigateToNextItem();
      } else {
        const element = this.document.activeElement as HTMLElement;
        element?.click();
      }
    }
  }

  /**
   * @description Will reset the response entered by the user.
   * This is achieved by setting focus to each response box and then press the backspace via code.
   */
  private clearResponses() {
    const keypadWrapper = this.getModalKeypad() ?? this.resolveKeyboardHost();
    const backspace = keypadWrapper?.querySelector('[data-symbol-key="backspace"]') as HTMLElement;

    if (!backspace) return;

    const inputWrapper = this.document.getElementsByClassName('lrn_response_input')?.[this.currentQuestionIndex];
    const spanList = inputWrapper?.getElementsByClassName('mq-root-block mq-last-focused') as HTMLCollectionOf<HTMLElement>;
    for (let i = 0; i < spanList?.length; i++) {
      spanList[i].replaceChildren();
      spanList[i].classList.add('mq-empty');
    }

    this.focusOnSpan(0);
    this.enableNumbPadButtons(true);
    this.currentSpanIndex = 0;
  }

  protected readonly TestletType = TestletType;
}
